/**
 * Environmental Geotech — Analysis Review  (Step 7 of the 8-step intelligence loop)
 *
 * Human-in-the-Loop gate: Environmental Officer reviews AI canopy analysis findings,
 * confirms deforestation extent / wetland loss severity, and authorises enforcement action.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck, ShieldCheck, MessageSquare,
  CheckCircle2, AlertTriangle, RefreshCw,
  ChevronDown, ChevronUp, Activity, TreePine,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MissionWithAnalysis {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  updated_at: string;
  metadata?: {
    ai_analysis_status?: string;
    step7_status?: string;
    diagnosis?: string;
    severity?: string;
    zone_name?: string;
    payload?: string;
    vertical?: string;
    canopy_loss_ha?: number;
    canopy_loss_pct?: number;
    burn_area_ha?: number;
    wetland_loss_ha?: number;
    [key: string]: any;
  };
}

// ─── Step 7 decision options ──────────────────────────────────────────────────

const STEP7_DECISIONS = [
  { id: 'confirm_critical',    label: 'Confirm Critical — Immediate Enforcement Required',         newStatus: 'confirmed_critical'   },
  { id: 'confirm_enforcement', label: 'Confirm — Issue Formal Enforcement Notice',                  newStatus: 'confirmed_enforcement' },
  { id: 'confirm_monitor',     label: 'Confirm — Elevated Monitoring (No Enforcement Yet)',         newStatus: 'confirmed_monitor'    },
  { id: 'override_low',        label: 'Override — Downgrade to Low Priority',                       newStatus: 'override_low'         },
  { id: 'false_positive',      label: 'Reject — False Positive',                                    newStatus: 'false_positive'       },
];

const DECISION_STYLE: Record<string, string> = {
  confirmed_critical:    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  confirmed_enforcement: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  confirmed_monitor:     'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  override_low:          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  false_positive:        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EnvAnalysisReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [missions, setMissions]     = useState<MissionWithAnalysis[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [reviewing, setReviewing]   = useState<MissionWithAnalysis | null>(null);
  const [decisionId, setDecisionId] = useState('');
  const [note, setNote]             = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions?mission_type=environmental_survey&limit=50');
      const all: MissionWithAnalysis[] = res.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'environmental' &&
            m.metadata?.ai_analysis_status === 'complete' &&
            !m.metadata?.step7_status
        )
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDecision = async () => {
    if (!reviewing || !decisionId) return;
    const dec = STEP7_DECISIONS.find((d) => d.id === decisionId)!;
    setSubmitting(true);
    try {
      await api.patch(`/api/missions/${reviewing.id}`, {
        metadata: {
          ...reviewing.metadata,
          step7_status:      dec.newStatus,
          step7_note:        note.trim(),
          step7_reviewed_at: new Date().toISOString(),
        },
      });
      setSuccess(`Step 7 decision recorded: ${dec.label}`);
      setReviewing(null);
      setDecisionId('');
      setNote('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to save decision');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = missions.length;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-green-600" /> Analysis Review
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 7 — Environmental Officer reviews AI canopy findings and authorises enforcement action
          </p>
          {pendingCount > 0 && (
            <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
              <Activity className="w-3 h-3" /> {pendingCount} awaiting review
            </span>
          )}
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Mission list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {missions.length} mission{missions.length !== 1 ? 's' : ''} awaiting Step 7 review
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : missions.length === 0 ? (
          <div className="text-center py-14">
            <ShieldCheck className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No missions pending review</p>
            <p className="text-sm text-gray-400 mt-1">AI analysis completes after data upload in <strong>Flight Data &amp; Analysis</strong></p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {missions.map((m) => {
              const isExpanded = expandedId === m.id;
              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.zone_name ?? '—'} · Updated {new Date(m.updated_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    {m.metadata?.severity && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        {m.metadata.severity}
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-4">
                      <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                          <TreePine className="w-3.5 h-3.5 text-green-600" /> AI Canopy Analysis Findings
                        </p>
                        {m.metadata?.diagnosis && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{m.metadata.diagnosis}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                          {m.metadata?.canopy_loss_ha != null && (
                            <span>Canopy loss: <strong className="text-red-600 dark:text-red-400">{Number(m.metadata.canopy_loss_ha).toFixed(1)} ha</strong></span>
                          )}
                          {m.metadata?.canopy_loss_pct != null && (
                            <span>Loss %: <strong className="text-red-600 dark:text-red-400">{Number(m.metadata.canopy_loss_pct).toFixed(1)}%</strong></span>
                          )}
                          {m.metadata?.burn_area_ha != null && (
                            <span>Burn area: <strong className="text-orange-600 dark:text-orange-400">{Number(m.metadata.burn_area_ha).toFixed(1)} ha</strong></span>
                          )}
                          {m.metadata?.wetland_loss_ha != null && (
                            <span>Wetland loss: <strong className="text-cyan-600 dark:text-cyan-400">{Number(m.metadata.wetland_loss_ha).toFixed(1)} ha</strong></span>
                          )}
                          <span>Payload: <strong className="text-gray-800 dark:text-gray-200">{m.metadata?.payload ?? '—'}</strong></span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button size="sm" onClick={() => { setReviewing(m); setDecisionId(''); setNote(''); }}>
                          <ClipboardCheck className="w-4 h-4 mr-1" /> Record Decision
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/missions/${m.id}`)}>
                          Open Mission
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Decision Modal ── */}
      {reviewing && (
        <Modal onClose={() => setReviewing(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Step 7 — Environmental Officer Review</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{reviewing.name}</p>
              </div>
              <ShieldCheck className="w-5 h-5 text-green-600" />
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Decision</p>
              {STEP7_DECISIONS.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDecisionId(d.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-all ${decisionId === d.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                >
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  {d.label}
                  {(d.id === 'confirm_critical' || d.id === 'confirm_enforcement') && (
                    <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${DECISION_STYLE[d.newStatus]}`}>
                      triggers Step 8
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" /> Officer Note
              </label>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Regulatory reference, jurisdiction, enforcement authority contact…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleDecision} disabled={!decisionId || submitting} loading={submitting} className="flex-1">
                Confirm Decision
              </Button>
              <Button variant="outline" onClick={() => setReviewing(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default EnvAnalysisReviewPage;

// Made with Bob
